import React, { CSSProperties, Dispatch, FC, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react'
import type { XYCoord, Identifier } from 'dnd-core'
import { getEmptyImage } from 'react-dnd-html5-backend'
import { useDrag, useDragLayer, useDrop } from 'react-dnd'

import Icon from 'components/Icon'
import Button from 'components/Button'
import ProgressSpinner from 'components/ProgressSpinner'

import VarietiesRow from './VarietiesRow'

import styles from './Draggable.module.css'

const CustomDragLayer = () => {
  const getItemStyles = (initialOffset: XYCoord | null, currentOffset: XYCoord | null) => {
    if (!initialOffset || !currentOffset) return { display: 'none' }
    const transform = `translate(${initialOffset.x}px, ${currentOffset.y}px)`
    return {
      transform,
      WebkitTransform: transform
    }
  }

  const { isDragging, initialOffset, currentOffset } = useDragLayer(monitor => ({
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging()
  }))
  if (!isDragging) return null

  const layerStyles: CSSProperties = {
    position: 'absolute',
    visibility: 'hidden'
  }

  return (
    <tr style={layerStyles}>
      <td style={getItemStyles(initialOffset, currentOffset)} />
    </tr>
  )
}

interface DraggableProps {
  children: ReactNode
}

export const Draggable: FC<DraggableProps> = ({ children }) => {
  return (
    <>
      {children}
      <CustomDragLayer />
    </>
  )
}

interface DraggableRowProps {
  productInfo: {
    id: string
    name: string
    initialIndex: number
    position: number
  }
  index: number
  loading: boolean
  disable?: boolean
  children: ReactNode
  varietyCount: number
  outOfStockThreshold: number | null | undefined
  rowChildren?: ReactNode

  className?: string
  setDraggedRow: Dispatch<SetStateAction<string | undefined>>
  handleDragRowChange: (dragIndex: number, hoverIndex: number) => void
  handleRowChange: (id: string, name: string, newPosition: number) => void
}

interface DraggableItemProps {
  index: number
  id: string
  type: string
  name: string
  initialIndex: number
  position: number
}

export const DraggableRow: React.FC<DraggableRowProps> = ({
  index,
  loading,
  className,
  productInfo,
  disable = false,
  handleRowChange,
  handleDragRowChange,
  setDraggedRow,
  children,
  varietyCount,
  outOfStockThreshold,
  rowChildren
}) => {
  const dragRef = useRef(null)
  const dropRef = useRef<HTMLTableRowElement>(null)
  const [showVariety, setShowVariety] = useState<boolean>(false)
  let draggedProduct: DraggableItemProps

  const [{ handlerId }, drop] = useDrop<DraggableItemProps, void, { handlerId: Identifier | null }>({
    accept: 'div',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      }
    },
    hover(item: DraggableItemProps, monitor) {
      if (!dragRef.current) return
      else {
        draggedProduct = item
        setDraggedRow(item.id)
      }
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = dropRef.current?.getBoundingClientRect()
      const hoverMiddleY = hoverBoundingRect && (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = hoverBoundingRect && (clientOffset as XYCoord).y - hoverBoundingRect.top
      if (hoverMiddleY != null && hoverClientY != null) {
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return
      }

      handleDragRowChange(dragIndex, hoverIndex)
      item.index = hoverIndex
    }
  })

  const [dragStyles, drag, preview] = useDrag({
    type: 'div',
    item: () => {
      return { ...productInfo, index }
    },
    collect: monitor => ({
      opacity: monitor.isDragging() ? 0.4 : 1,
      border: monitor.isDragging() ? '2px solid var(--colorBlueDark)' : 'none'
    }),
    end: () => {
      const newPosition = draggedProduct.position + (draggedProduct.index - draggedProduct.initialIndex)
      if (newPosition !== draggedProduct.position) {
        handleRowChange(draggedProduct.id, draggedProduct.name, newPosition)
      }
    }
  })

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
  }, [preview])

  drop(dropRef)
  drag(dragRef)

  return (
    <>
      <tr ref={dropRef} data-handler-id={handlerId} className={className} style={dragStyles}>
        {children}

        {varietyCount > 1 ? (
          <>
            <td className={styles.right} colSpan={2}>
              <Button
                className={styles.variantsButton}
                kind="link"
                onClick={() => {
                  setShowVariety(prevState => !prevState)
                }}>
                {!showVariety ? `Show ` : `Hide `} {varietyCount} Variants
              </Button>
            </td>
            {rowChildren}
          </>
        ) : null}

        <td>
          <div className={styles.positionAdjustmentIconWrapper} ref={dragRef}>
            {disable ? (
              <div className={styles.iconPlaceHolder} />
            ) : loading ? (
              <div className={styles.iconPlaceHolder}>
                <ProgressSpinner />
              </div>
            ) : (
              <Icon kind="menu" size={16} className={styles.positionAdjustmentIcon} />
            )}
          </div>
        </td>
      </tr>
      {showVariety ? (
        <VarietiesRow className={className} productId={productInfo.id} outOfStockThreshold={outOfStockThreshold} />
      ) : null}
    </>
  )
}
